import { useContext, useEffect, useMemo, useState } from "react"

// ** Third Party Components
import axios from "axios"
import { he } from "date-fns/locale"
import { parseCookies } from "nookies"
import { formatDistanceToNow } from "date-fns"
import { useMutation, useQuery } from "@tanstack/react-query"
import { ChevronUp, Download, SquarePen, LayoutList, FileSpreadsheet, Pencil, Check, X } from "lucide-react"
import { Link, useNavigate, useSearchParams } from "react-router-dom"

// ** UI Components
import { Switch } from "../common/switch"
import { Alert } from "@components/common/alert"
import { Input } from "@components/common/input"
import PmsLoader from "@components/common/loader"
import { Button } from "@components/common/button"
import { Avatar, AvatarFallback, AvatarImage } from "@components/common/avatar"
import { Tooltip, TooltipContent, TooltipTrigger } from "@components/common/tooltip"
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@components/common/card"

// ** Api Routes
import { FINANCIAL_SERVER_URL, BILLS_BY_BOQ_ID__LIST_ENDPOINT } from "@pma/utils/api-routes"

// ** Constants
import { ACCESS_TOKEN } from "@pma/utils/constants"

// ** Utils
import { formatCurrency } from "@pma/utils/utils"
import { USERS_PAGE_ROUTE } from "@pma/utils/page-routes"
import {
  cn,
  computeBillTotal, getAccumulatedTotal,
  exportToExcel,
  getUserFromId,
  getPreviouslyPaidTotal,
} from "@pma/utils/helpers"

// ** Store
import { useAuth } from "@pma/stores/auth-store"

// ** Types
import { DictType } from "@pma/types"

// ** Context
import { UserRoleContext } from "@pma/contexts/UserRoleContext"

// ** Custom Hooks
import { toast } from "@pma/hooks/use-toast"
import useTranslator from "@pma/hooks/use-translator"
import { usePermissions } from "@pma/hooks/use-permissions"
import BillRow from "./bill-row"
import BoQEdit from "./edit-bill"
import BillPrintTemplate from "./bill-print-template"
import { BillStatusBadge } from "./bill-status-badge"

interface BoQDetailsProps {
  boqId: string
  mainBoQDetails: any
  allBillsOnBoq?: any[]
}

export function AllBills({ boqId, mainBoQDetails, allBillsOnBoq }: BoQDetailsProps) {
  const navigate = useNavigate()
  const { authConfig } = useAuth()
  const [searchParam] = useSearchParams()
  const { translate, locale } = useTranslator()
  const { currentOrg } = usePermissions()

  const { userRoleDetails } = useContext(UserRoleContext)

  const isHebrew = locale === "he"
  const queryKey = searchParam.get("revalidate")
  const accessToken = parseCookies()[ACCESS_TOKEN]
  const newParams = new URLSearchParams(searchParam)
  const organizationUsers = authConfig?.organizationUsers
  const userRole = currentOrg

  const [searchKey, setSearchKey] = useState("")
  const [showDeleted, setShowDeleted] = useState(false)
  const [editBillType, setEditBillType] = useState<string>("")
  const [selectBillId, setSelectedBillId] = useState<number>(0)
  const [showEditBillDialog, setEditBillDialog] = useState(false)
  const [expandedCards, setExpandedCards] = useState<{ [key: string]: boolean }>({})
  const [editingBillId, setEditingBillId] = useState<number | null>(null)
  const [editingBillNumber, setEditingBillNumber] = useState<string>("")

  const toggleCard = (id: string | number) => {
    setExpandedCards((prev) => ({ ...prev, [id]: !prev[id] }))
  }

  const getAllBills = async () => {
    const response = await axios.post(
      FINANCIAL_SERVER_URL + BILLS_BY_BOQ_ID__LIST_ENDPOINT,
      {
        boq_id: boqId,
        type: "all",
        show_deleted: showDeleted,
      },
      {
        headers: {
          Authorization: "Bearer " + accessToken,
          "Content-Type": "application/json",
        },
      },
    )
    return response.data
  }

  const {
    data,
    isLoading: isLoadingAllBills,
    refetch: refetchAllBills,
  } = useQuery({
    queryKey: ["all-bills", boqId, showDeleted],
    queryFn: getAllBills,
  })

  const renameBillMutation = useMutation({
    mutationKey: ["rename-bill"],
    mutationFn: async ({ bill_id, number }: { bill_id: number; number: string }) => {
      const response = await axios.post(
        FINANCIAL_SERVER_URL + "/bill/new/rename",
        { bill_id, number },
        {
          headers: {
            Authorization: "Bearer " + accessToken,
            "Content-Type": "application/json",
          },
        },
      )
      return response.data
    },
    onSuccess: () => {
      toast({
        title: translate("Success"),
        description: translate("Bill number updated successfully"),
        variant: "success",
      })
      setEditingBillId(null)
      setEditingBillNumber("")
      refetchAllBills()
    },
    onError: (error: DictType) => {
      const errorMessage = error?.response?.data?.message
      toast({
        title: translate("Error"),
        description: errorMessage || translate("Failed to update bill number"),
        variant: "error",
      })
    },
  })

  const sortBills = data?.items?.sort(
    (a: DictType, b: DictType) =>
      new Date(b?.created_at).getTime() - new Date(a?.created_at).getTime(),
  )

  const allBills =
    sortBills?.filter(
      (bill: DictType) => {
        const matchesSearch =
          bill?.number?.toString().includes(searchKey) ||
          bill?._created_by?.name?.toLowerCase().includes(searchKey.toLowerCase()) ||
          bill?._declined_by?.name?.toLowerCase().includes(searchKey.toLowerCase()) ||
          bill?._supervisor?.name?.toLowerCase().includes(searchKey.toLowerCase())
        return matchesSearch
      },
    ) || []

  useEffect(() => {
    if (queryKey === "bills") {
      refetchAllBills()
      newParams.delete("revalidate")
      navigate(`?${newParams.toString()}`)
    }
  }, [queryKey])

  const exportBillsList = () => {
    const bills = allBills || []
    if (bills.length === 0) return

    const headers = isHebrew
      ? ["מספר חשבון", "סטטוס", "קבלן", "סכום מבוקש", "סכום מאושר", "מצטבר מחשבונות קודמים", "עכבון %", "סכום עכבון", "פריטים", "תאריך יצירה", "נוצר על ידי"]
      : ["Bill Number", "Status", "Contractor", "Requested Amount", "Approved Amount", "Accumulated from Previous", "Retention %", "Retention Amount", "Items", "Created", "Created By"]

    const csvRows = bills.map((bill: DictType) => {
      const requestedSum = computeBillTotal(bill, true)
      const approvedSum = computeBillTotal(bill, false)
      const accumulatedFromPrevious = getPreviouslyPaidTotal(bill, allBillsOnBoq)
      const retentionPercent = bill?.retention_percent || mainBoQDetails?.retention_percent || 0
      const retentionAmount = approvedSum * (retentionPercent / 100)

      return [
        bill?.number || "",
        translate(bill?.status) || "",
        bill?._contractor_company?.name || bill?._created_by?.name || "",
        requestedSum,
        approvedSum,
        accumulatedFromPrevious,
        retentionPercent,
        retentionAmount,
        bill?._bill_items?.length || 0,
        bill?.created_at ? new Date(bill.created_at).toLocaleDateString("he-IL") : "",
        bill?._created_by?.name || "",
      ]
    })

    const csvContent = [headers, ...csvRows]
      .map((row) => row.map((cell: any) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n")

    const BOM = "\uFEFF"
    const blob = new Blob([BOM + csvContent], { type: "text/csv;charset=utf-8;" })
    const url = URL.createObjectURL(blob)
    const link = document.createElement("a")
    link.href = url
    link.download = `bills-export-${new Date().toISOString().slice(0, 10)}.csv`
    link.click()
    URL.revokeObjectURL(url)
  }

  const showEditButton = (bill: any) => {
    let show = false

    if (bill) {
      if (
        bill?.status === "pending" &&
        (userRoleDetails?.supervisor || userRoleDetails?.fin_manager)
      ) {
        show = true
      }

      if (
        bill?.status === "new" &&
        (userRoleDetails?.constructor ||
          userRoleDetails?.supervisor ||
          userRoleDetails?.fin_manager)
      ) {
        show = true
      }
    }

    return show
  }

  return (
    <div className='space-y-6'>
      <Card>
        <CardHeader className='flex flex-col gap-4 sm:flex-row sm:justify-between'>
          <div className='w-full flex-1'>
            <CardTitle>{translate("All Bills")}</CardTitle>
            <CardDescription>{translate("View and review all bills")}</CardDescription>
          </div>

          <div className='flex items-center'>
            <div className='flex items-center gap-2'>
              <Switch
                checked={showDeleted}
                className='h-6 w-11'
                onCheckedChange={(checked) => setShowDeleted(checked)}
              />
              <h4 className='text-sm font-semibold'>{translate("Show Deleted Bills")}</h4>
            </div>
          </div>

          <div className='flex items-center gap-2 w-full sm:w-auto'>
            <Input
              type='text'
              placeholder={translate("Search Bills")}
              value={searchKey}
              onChange={(e) => setSearchKey(e.target.value)}
              className='w-full sm:w-64 lg:w-80'
            />
            <Button
              variant='outline'
              size='sm'
              onClick={exportBillsList}
              disabled={!allBills || allBills.length === 0}
              className='shrink-0'
            >
              <FileSpreadsheet className={cn("h-4 w-4", isHebrew ? "ml-1" : "mr-1")} />
              <span className='hidden sm:inline'>{translate("Export Bills")}</span>
              <span className='sm:hidden'>{translate("Export")}</span>
            </Button>
          </div>
        </CardHeader>

        <CardContent>
          <div className='grid gap-2 space-y-4'>
            {allBills?.length > 0 &&
              !isLoadingAllBills &&
              allBills?.map((bill: DictType, index: number) => {
                return (
                <Tooltip key={index}>
                  <TooltipTrigger asChild>
                    <Card
                      key={index}
                      className={cn("overflow-hidden hover:shadow-md hover:border-primary/20 transition-all duration-200", bill.deleted ? "bg-red-100" : "")}
                    >
                      <div className='p-2 sm:p-3 md:p-2'>
                        <div className='flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between'>
                          {/* Bill Info Section */}
                          <div
                            className='flex min-w-0 flex-1 cursor-pointer flex-col p-2'
                            onClick={() => toggleCard(bill?.id)}
                          >
                            <div className='mb-2 flex flex-wrap items-center gap-2 sm:gap-4'>
                              {editingBillId === bill?.id ? (
                                <div className='flex items-center gap-1' onClick={(e) => e.stopPropagation()}>
                                  <span className='text-base font-semibold sm:text-lg md:text-xl'>#</span>
                                  <Input
                                    autoFocus
                                    value={editingBillNumber}
                                    onChange={(e) => setEditingBillNumber(e.target.value)}
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter" && editingBillNumber.trim()) {
                                        renameBillMutation.mutate({
                                          bill_id: bill?.id,
                                          number: editingBillNumber.trim(),
                                        })
                                      }
                                      if (e.key === "Escape") {
                                        setEditingBillId(null)
                                        setEditingBillNumber("")
                                      }
                                    }}
                                    className='h-7 w-24 text-base font-semibold sm:w-32'
                                  />
                                  <button
                                    type='button'
                                    className='inline-flex items-center justify-center rounded-md p-1 text-green-600 hover:bg-green-100 transition-colors disabled:opacity-50'
                                    disabled={!editingBillNumber.trim() || renameBillMutation.isPending}
                                    onClick={() => {
                                      renameBillMutation.mutate({
                                        bill_id: bill?.id,
                                        number: editingBillNumber.trim(),
                                      })
                                    }}
                                  >
                                    <Check className='h-4 w-4' />
                                  </button>
                                  <button
                                    type='button'
                                    className='inline-flex items-center justify-center rounded-md p-1 text-red-600 hover:bg-red-100 transition-colors'
                                    onClick={() => {
                                      setEditingBillId(null)
                                      setEditingBillNumber("")
                                    }}
                                  >
                                    <X className='h-4 w-4' />
                                  </button>
                                </div>
                              ) : (
                                <div className='flex items-center gap-1'>
                                  <h2
                                    className={
                                      "break-all text-base font-semibold sm:text-lg md:text-xl"
                                    }
                                  >
                                    #{bill?.number}
                                  </h2>
                                  <button
                                    type='button'
                                    className='inline-flex items-center justify-center rounded-md p-1 text-muted-foreground hover:text-primary hover:bg-muted transition-colors'
                                    onClick={(e) => {
                                      e.stopPropagation()
                                      setEditingBillId(bill?.id)
                                      setEditingBillNumber(bill?.number?.toString() || "")
                                    }}
                                    title={translate("Edit Bill Number")}
                                  >
                                    <Pencil className='h-3.5 w-3.5' />
                                  </button>
                                </div>
                              )}
                              <span className='hidden text-sm sm:inline sm:text-base'>-</span>
                              <span className='break-all text-sm font-semibold font-mono tabular-nums sm:text-base md:text-lg'>
                                {formatCurrency(computeBillTotal(bill, true), isHebrew)}
                              </span>
                            </div>

                            <div className='flex flex-wrap items-center gap-2'>
                              <BillStatusBadge
                                status={bill?.status}
                                isApproved={bill?.is_approved}
                                isSigned={bill?.is_signed}
                                isDeclined={bill?.is_declined}
                                isReturnedToContractor={bill?.is_returned_to_contractor}
                              />
                              <small className='whitespace-nowrap text-xs text-muted-foreground'>
                                ({bill?._bill_items?.filter((i: any) => i?.amount)?.length || 0}) {translate("Items")}
                              </small>
                            </div>
                            <div className='flex flex-wrap items-center gap-2 mt-1'>
                              <small className='text-xs text-blue-500 font-mono'>
                                {translate("total accumulated including this bill")}: {formatCurrency(
                                  getAccumulatedTotal(bill, allBillsOnBoq),
                                  isHebrew
                                )}
                              </small>
                            </div>
                          </div>

                          {/* Actions Section */}
                          <div className='flex min-w-0 flex-col gap-2 sm:items-end'>
                            <div className='flex flex-wrap gap-2'>
                              {showEditButton(bill) ? (
                                <Button
                                  variant='default'
                                  size='sm'
                                  onClick={() => {
                                    setEditBillType(
                                      bill?.status === "new" ? "contractor" : "approval",
                                    )
                                    setSelectedBillId(bill.id)
                                    setEditBillDialog(!showEditBillDialog)
                                  }}
                                >
                                  <SquarePen className='mx-1 h-4 w-4' />
                                  {translate("Edit Bill")}
                                </Button>
                              ) : null}
                              {bill?.number && (
                                <BillPrintTemplate bill={bill} mainBoQDetails={mainBoQDetails} status={bill?.status} />
                              )}
                              <Button
                                variant={"default"}
                                size={"sm"}
                                onClick={() => {
                                  const value = bill?._bill_items.filter(
                                    (item: DictType) => item?.amount,
                                  )
                                  exportToExcel(value, "bills", {}, isHebrew)
                                }}
                                className='text-xs'
                              >
                                <Download className={`mx-1 h-3 w-3 sm:h-4 sm:w-4`} />
                                <span className='hidden sm:inline'>{translate("Download")}</span>
                              </Button>
                              <Button
                                variant='ghost'
                                size='sm'
                                onClick={() => toggleCard(bill?.id)}
                              >
                                <ChevronUp
                                  className={`h-5 w-5 transition-transform sm:h-6 sm:w-6 ${expandedCards[bill?.id as string] ? "" : "rotate-180"}`}
                                />
                              </Button>
                            </div>
                            <small className='break-words text-xs text-muted-foreground'>
                              {translate("Created")}
                              {": "}
                              {formatDistanceToNow(new Date(bill?.created_at), {
                                addSuffix: true,
                                locale: isHebrew ? he : undefined,
                              })}
                            </small>
                          </div>
                        </div>

                        {expandedCards[bill?.id as string] && (
                          <div className='mt-4 space-y-4'>
                            {/* Alert Section */}
                            {(bill?.returned?.reason || bill?.resubmission?.resubmitted_by) &&
                            bill?.status !== "signed" ? (
                              <div className='w-full'>
                                <Alert className='w-full' variant={"destructive"}>
                                  <span className='break-words text-xs sm:text-sm'>
                                    {translate(
                                      bill?.resubmission?.resubmitted_by
                                        ? "Bill has been resubmitted by"
                                        : "Bill has been returned by",
                                    )}{" "}
                                    {bill?.resubmission?.resubmitted_by
                                      ? bill?.resubmission?._resubmitted_by?.name
                                      : bill?.returned?._returned_by?.name}
                                    ({translate(userRole?.role?.name)})
                                  </span>
                                </Alert>
                              </div>
                            ) : null}

                            {/* Summary Section */}
                            <div className='flex flex-col gap-4 px-0 sm:px-2'>
                              <div className='flex w-full flex-col gap-4 md:flex-row md:justify-between'>
                                {/* Left Section - Approved Sum and Notes */}
                                <div className='min-w-0 flex-1 gap-1'>
                                  <div className='flex flex-wrap items-center gap-2'>
                                    <strong className='whitespace-nowrap text-xs sm:text-sm'>
                                      {translate("Approved Sum")}:
                                    </strong>
                                    <span className='break-all text-sm font-semibold text-green-400 sm:text-base md:text-lg'>
                                      {formatCurrency(computeBillTotal(bill, false), isHebrew)}
                                    </span>
                                  </div>
                                  <div className='mt-0 flex flex-wrap items-center gap-2'>
                                    <strong className='whitespace-nowrap text-xs sm:text-sm'>
                                      {translate("Bill Number")}:
                                    </strong>
                                    <span className='text-sm font-semibold sm:text-base'>
                                      {bill?.number}
                                    </span>
                                  </div>
                                  <div className='mt-0 flex flex-wrap items-center gap-2'>
                                    <strong className='whitespace-nowrap text-xs sm:text-sm'>
                                      {translate("Accumulated from previous bills")}:
                                    </strong>
                                    <span className='text-sm font-semibold text-blue-500 sm:text-base'>
                                      {formatCurrency(
                                        getPreviouslyPaidTotal(bill, allBillsOnBoq),
                                        isHebrew
                                      )}
                                    </span>
                                  </div>
                                  {bill?.is_signed && bill?.signed_at && (
                                    <div className='mt-0 flex items-center gap-2'>
                                      <strong className='whitespace-nowrap text-xs sm:text-sm'>
                                        {translate("signed")}:
                                      </strong>
                                      <span className='text-xs text-muted-foreground font-mono'>
                                        {new Date(Number(bill.signed_at)).toLocaleDateString("he-IL", { day: "2-digit", month: "2-digit", year: "numeric" })}{" "}
                                        {new Date(Number(bill.signed_at)).toLocaleTimeString("he-IL", { hour: "2-digit", minute: "2-digit" })}
                                      </span>
                                    </div>
                                  )}

                                  {(bill?.message?.text || bill?.returned?.reason) && (
                                    <div className='space-y-2'>
                                      <div className='break-words'>
                                        <strong className='text-xs sm:text-sm'>
                                          {translate("Note")}:
                                        </strong>{" "}
                                        <span className='text-xs sm:text-sm'>
                                          {bill?.message?.text || "---"}
                                        </span>
                                      </div>
                                      {bill?.message?.user_id ? (
                                        <Link
                                          to={USERS_PAGE_ROUTE + `/${bill?.message?.user_id}`}
                                          className='flex w-fit items-center gap-2 text-xs hover:underline'
                                        >
                                          <Avatar className={`h-6 w-6 flex-shrink-0 sm:h-7 sm:w-7`}>
                                            <AvatarImage
                                              src={
                                                getUserFromId(
                                                  bill?.message?.user_id,
                                                  organizationUsers,
                                                ).user_picture
                                                  ? getUserFromId(
                                                      bill?.message?.user_id,
                                                      organizationUsers,
                                                    ).user_picture.url
                                                  : `${process.env.PUBLIC_URL}/usr.svg`
                                              }
                                              alt='Avatar'
                                            />
                                            <AvatarFallback className='text-xs uppercase'>
                                              {
                                                getUserFromId(
                                                  bill?.message?.user_id,
                                                  organizationUsers,
                                                )?.first_name?.[0]
                                              }
                                              {
                                                getUserFromId(
                                                  bill?.message?.user_id,
                                                  organizationUsers,
                                                )?.last_name?.[0]
                                              }
                                            </AvatarFallback>
                                          </Avatar>
                                          <span className='truncate'>
                                            {
                                              getUserFromId(
                                                bill?.message?.user_id,
                                                organizationUsers,
                                              )?.name
                                            }
                                          </span>
                                        </Link>
                                      ) : null}
                                    </div>
                                  )}
                                </div>

                                {/* Right Section - Resubmission Info */}
                                {bill?.resubmission?.resubmitted_at &&
                                  bill?.status !== "signed" && (
                                    <div className='min-w-0 flex-shrink-0 space-y-2'>
                                      <div className='break-words text-xs sm:text-sm'>
                                        <small>{translate("Resubmitted")}</small>
                                        {": "}
                                        <small>
                                          {formatDistanceToNow(
                                            new Date(bill?.resubmission?.resubmitted_at),
                                            {
                                              addSuffix: true,
                                              locale: isHebrew ? he : undefined,
                                            },
                                          )}
                                        </small>
                                      </div>

                                      <Link
                                        to={
                                          USERS_PAGE_ROUTE +
                                          `/${bill?.resubmission?._resubmitted_by?.id}`
                                        }
                                        className='flex w-fit items-center gap-2 text-xs hover:underline'
                                      >
                                        <Avatar className={`h-6 w-6 flex-shrink-0 sm:h-7 sm:w-7`}>
                                          <AvatarImage
                                            src={
                                              getUserFromId(
                                                bill?.resubmission?._resubmitted_by?.id,
                                                organizationUsers,
                                              ).user_picture
                                                ? getUserFromId(
                                                    bill?.resubmission?._resubmitted_by?.id,
                                                    organizationUsers,
                                                  ).user_picture.url
                                                : `${process.env.PUBLIC_URL}/usr.svg`
                                            }
                                            alt='Avatar'
                                          />
                                          <AvatarFallback className='text-xs uppercase'>
                                            {
                                              getUserFromId(
                                                bill?.resubmission?._resubmitted_by?.id,
                                                organizationUsers,
                                              )?.first_name?.[0]
                                            }
                                            {
                                              getUserFromId(
                                                bill?.resubmission?._resubmitted_by?.id,
                                                organizationUsers,
                                              )?.last_name?.[0]
                                            }
                                          </AvatarFallback>
                                        </Avatar>
                                        <span className='truncate'>
                                          {
                                            getUserFromId(
                                              bill?.resubmission?._resubmitted_by?.id,
                                              organizationUsers,
                                            )?.name
                                          }
                                        </span>
                                      </Link>
                                    </div>
                                  )}
                              </div>
                            </div>

                            {/* Table Header - Hidden on mobile, shown on larger screens */}
                            <div className='hidden grid-cols-12 gap-2 border-b bg-muted/50 p-2 text-xs font-medium lg:grid'>
                              <div className='col-span-1'>#</div>
                              <div className='col-span-2'>{translate("Number")}</div>
                              <div className='col-span-1'>{translate("Company")}</div>
                              <div className='col-span-1'>{translate("Project")}</div>
                              <div className='col-span-1'>{translate("Contractor")}</div>
                              <div className='col-span-1'>{translate("Supervisor")}</div>
                              {(bill?.status === "pending" ||
                                bill?.status === "approved" ||
                                bill?.status === "signed") && (
                                <div className='col-span-2 text-center'>
                                  {translate("Retention")}
                                </div>
                              )}
                              <div
                                className={`${bill?.status === "pending" || bill?.status === "approved" || bill?.status === "signed" ? "col-span-3" : "col-span-5"} text-center`}
                              >
                                {translate("Actions")}
                              </div>
                            </div>

                            {/* Bill Row Component */}
                            <div className='w-full'>
                              <div className='w-full'>
                                <BillRow
                                  bill={bill}
                                  revalidate={refetchAllBills}
                                  parentExpanded={!!expandedCards[bill?.id as string]}
                                  status='all'
                                  allBills={allBills}
                                  allBillsOnBoq={allBillsOnBoq}
                                />
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </Card>
                  </TooltipTrigger>
                  <TooltipContent
                    className={cn(
                      bill?.is_declined
                        ? "bg-red-500"
                        : bill?.is_signed
                          ? "bg-green-500"
                          : bill?.is_approved
                            ? "bg-primary"
                            : "bg-muted-foreground",
                      "max-w-[250px] text-xs sm:max-w-none sm:text-sm",
                    )}
                  >
                    {bill?.is_declined || bill?.is_signed ? (
                      <>
                        {translate(
                          bill?.is_declined ? "Bill Declined by Supervisor" : "Bill Signed",
                        )}
                      </>
                    ) : (
                      <>
                        {translate("Next Action")}:{" "}
                        {translate(
                          bill?.is_approved
                            ? "Requires Signing/Decline/Return"
                            : bill?.status === "new"
                              ? "Edit/Move to approve"
                              : "Requires Approval/Return",
                        )}
                      </>
                    )}
                  </TooltipContent>
                </Tooltip>
              )})}
          </div>

          {isLoadingAllBills && <PmsLoader />}
          {allBills.length < 1 && !isLoadingAllBills && (
            <div className='flex flex-col items-center justify-center py-12 text-center'>
              <div className='rounded-full bg-gray-100 p-4 mb-4'>
                <LayoutList className='h-8 w-8 text-gray-400' />
              </div>
              <p className='text-sm font-medium text-gray-900'>{translate("no bills yet")}</p>
              <p className='mt-1 text-xs text-gray-500'>{translate("empty_all_bills_hint")}</p>
            </div>
          )}
        </CardContent>
        <CardFooter className='flex flex-wrap justify-between gap-2'>
          <div className='break-words text-xs text-muted-foreground sm:text-sm'>
            {translate("Showing")} {allBills?.length} {translate("of")} {allBills?.length}{" "}
            {translate("items")}
          </div>
        </CardFooter>
      </Card>

      {showEditBillDialog && (
        <BoQEdit
          open={showEditBillDialog}
          onOpenChange={setEditBillDialog}
          boqId={boqId}
          type={editBillType}
          currentBill={allBills.find((bill: any) => bill.id === selectBillId)}
        />
      )}

    </div>
  )
}
